import { Command } from 'commander'
import { createApiClient, SCOPES } from '../http.js'
import { loadConfig, saveConfig } from '../config.js'
import { generateSigningKey, loadKey, saveKey } from '../crypto/keys.js'
import { requireMyPublisher } from '../registry.js'

export function registerKeygen(program: Command): void {
  program
    .command('keygen')
    .description('Generate an Ed25519 signing key and register its public half with your publisher profile')
    .option('--name <name>', 'key name (stored at ~/.verentis/keys/<name>.json)', 'default')
    .option('--no-register', 'skip registering the public key with the marketplace')
    .option('--set-default', 'use this key as the default for `verentis pack`', true)
    .action(async (options: { name: string; register: boolean; setDefault: boolean }) => {
      const key = generateSigningKey()
      const path = await saveKey(options.name, key)
      console.log(`Generated Ed25519 key '${options.name}' (kid ${key.kid})`)
      console.log(`  private key: ${path} — keep this safe; it never leaves your machine.`)

      if (options.setDefault) {
        const config = await loadConfig()
        config.defaultKey = options.name
        await saveConfig(config)
      }

      if (options.register) {
        const api = await createApiClient()
        const publisher = await requireMyPublisher(api)
        await api.request('POST', `/v1/publishers/${publisher.id}/keys`, {
          scopes: [SCOPES.publisherUpdate],
          body: { publisherId: publisher.id, algorithm: 'Ed25519', publicKey: key.publicKey },
        })
        console.log(`Registered public key with publisher '${publisher.name}'.`)
      } else {
        console.log('Public key NOT registered (use `verentis key register` later).')
      }
    })

  const keyCmd = program.command('key').description('Manage signing keys')

  keyCmd
    .command('register')
    .description("Register a local key's public half with your publisher profile")
    .option('--name <name>', 'key name', 'default')
    .action(async (options: { name: string }) => {
      const key = await loadKey(options.name)
      const api = await createApiClient()
      const publisher = await requireMyPublisher(api)
      await api.request('POST', `/v1/publishers/${publisher.id}/keys`, {
        scopes: [SCOPES.publisherUpdate],
        body: { publisherId: publisher.id, algorithm: 'Ed25519', publicKey: key.publicKey },
      })
      console.log(`Registered key '${options.name}' (kid ${key.kid}) with publisher '${publisher.name}'.`)
    })

  keyCmd
    .command('revoke <kid>')
    .description('Revoke a registered signing key')
    .action(async (kid: string) => {
      const api = await createApiClient()
      const publisher = await requireMyPublisher(api)
      await api.request('DELETE', `/v1/publishers/${publisher.id}/keys/${encodeURIComponent(kid)}`, {
        scopes: [SCOPES.publisherUpdate],
      })
      console.log(`Revoked key ${kid}.`)
    })
}
